import {
  Alert,
  Button,
  Radio,
  StyledReactSelect,
} from '@fountain/fountain-ui-components';
import { Icon, TrashOutlineIcon } from '@fountain/ripple/icon';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Chip,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { WorkflowRehireCheckStage } from 'api-clients/monolith';
import produce from 'immer';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { StageSettingCard } from 'containers/WorkflowEditor/components';
import { RulesEditDataContext } from 'containers/WorkflowEditor/contexts/rulesEditDataContext';
import { StageContext } from 'containers/WorkflowEditor/contexts/stageContext';
import {
  RehireConnector,
  useRehireConnectors,
} from 'hooks/useRehireConnectors';
import {
  RehireMetadataErrorReason,
  useRehireMetadata,
} from 'hooks/useRehireMetadata';

import { DryRunTester } from './DryRunTester';
import { messages } from './messages';
import { ReportStageSettings } from './ReportStageSettings';
import { useStyles } from './styles';

export type OnUnavailableOption = 'hold' | 'continue_to_rules';

type EnrichmentSource =
  WorkflowRehireCheckStage['additional_info']['allowed_enrichment_sources'][number];
type AlwaysCopiedDataKey =
  WorkflowRehireCheckStage['additional_info']['always_copied_data_keys'][number];

interface SsnSourceOption {
  key: string;
  predefined: boolean;
}

const SSN_STANDARD_FIELD: SsnSourceOption = { key: 'ssn', predefined: true };

interface EnrichmentMapping {
  source: EnrichmentSource;
  target: string;
}

interface MatchIdentifier {
  kind: string;
  source: string;
}

const MAX_ENRICHMENT_MAPPINGS = 5;

const ENRICHMENT_SOURCE_LABELS: Record<EnrichmentSource, MessageDescriptor> = {
  manager: messages.enrichmentSourceManager,
  work_location: messages.enrichmentSourceWorkLocation,
  employment_type: messages.enrichmentSourceEmploymentType,
};

const DEFAULT_ENRICHMENT_TARGETS: Record<EnrichmentSource, string> = {
  manager: 'previous_manager',
  work_location: 'previous_location',
  employment_type: 'previous_employment_type',
};

const ALWAYS_COPIED_DESCRIPTIONS: Record<
  AlwaysCopiedDataKey,
  MessageDescriptor
> = {
  rehire_check_employment_status:
    messages.alwaysCopiedEmploymentStatusDescription,
  rehire_check_termination_date:
    messages.alwaysCopiedTerminationDateDescription,
  rehire_check_original_hire_date:
    messages.alwaysCopiedOriginalHireDateDescription,
  rehire_check_matched_id: messages.alwaysCopiedMatchedIdDescription,
  rehire_check_matched_employee_number:
    messages.alwaysCopiedMatchedEmployeeNumberDescription,
};

const METADATA_ERROR_MESSAGES: Record<
  RehireMetadataErrorReason,
  MessageDescriptor
> = {
  no_connector: messages.metadataRetryLaterNotice,
  connector_ambiguous: messages.metadataRetryLaterNotice,
  filter_unsupported_suspected: messages.metadataRetryLaterNotice,
  identifier_mismatch: messages.metadataRetryLaterNotice,
  multiple_distinct_identities: messages.metadataRetryLaterNotice,
  setup_sync_pending: messages.metadataSetupSyncPendingNotice,
  qa_failed: messages.metadataQaFailedNotice,
  connector_invalid: messages.metadataConnectorInvalidNotice,
  permission_missing: messages.metadataConnectorInvalidNotice,
  kombo_rate_limited: messages.metadataRetryLaterNotice,
  kombo_error: messages.metadataRetryLaterNotice,
};

// The stage-specific `extra` fields below are accessed through RehireCheckStageExtra, following
// the same typed-local-cast pattern ComplianceStage already uses for its own extra fields not
// covered by the shared WorkflowBaseStage.extra type (e.g. configured_requirement_uuids).
export interface RehireCheckStageExtra {
  connector_uuid?: string | null;
  eligibility_field?: string | null;
  eligible_values?: string[] | null;
  match_identifiers?: MatchIdentifier[] | null;
  require_ssn?: boolean | null;
  on_unavailable?: OnUnavailableOption | null;
  shadow_mode?: boolean | null;
  enrichment_mappings?: EnrichmentMapping[] | null;
}

export interface RehireCheckStageProps {
  stage: WorkflowRehireCheckStage;
}

function getExtra(stage: WorkflowRehireCheckStage): RehireCheckStageExtra {
  return stage.extra as RehireCheckStageExtra;
}

export function RehireCheckStageSettings({ stage }: RehireCheckStageProps) {
  const whoami = useSelector(makeSelectWhoami());
  const { stageLoadRevision } = useContext(StageContext);
  const { funnelSlug } = useParams<{ funnelSlug?: string }>();
  const enabled = whoami.feature_flags?.['rehire-check-report-source'] === true;
  const report =
    Boolean(stage.extra?.report_config) ||
    ![null, undefined, 'synced'].includes(stage.extra?.source_mode);
  if (!enabled && !report) return <SyncedSettings stage={stage} />;
  return (
    <ReportStageSettings
      key={`${whoami.account_id}:${funnelSlug ?? ''}:${stage.external_id}:${stageLoadRevision ?? 0}`}
      stage={stage}
      enabled={enabled}
      accountId={whoami.account_id}
    >
      <SyncedSettings stage={stage} />
    </ReportStageSettings>
  );
}

function SyncedSettings({ stage }: RehireCheckStageProps) {
  const styles = useStyles();
  const intl = useIntl();
  const { setStage, setCanSave, isDirty } = useContext(StageContext);
  const { allow_read_secure_data: allowReadSecureData, data_keys: dataKeys } =
    useContext(RulesEditDataContext);
  // Only `key` and `predefined` reach the select: react-select renders any
  // item carrying `options` (a dropdown field's answer choices) as a group.
  // `ssn` is secure data, which the data key lists never carry, so the
  // standard field is added here.
  const dataKeyOptions: SsnSourceOption[] = [
    SSN_STANDARD_FIELD,
    ...[...(dataKeys?.standard_keys ?? []), ...(dataKeys?.custom_keys ?? [])]
      .filter(({ key }) => key !== SSN_STANDARD_FIELD.key)
      .map(({ key, predefined }) => ({ key, predefined }))
      .sort((left, right) => left.key.localeCompare(right.key)),
  ];
  const { connectors, loading: connectorsLoading } = useRehireConnectors();

  const extra = getExtra(stage);
  const eligibilityField = extra.eligibility_field ?? '';
  const eligibleValues = extra.eligible_values ?? [];
  const onUnavailable: OnUnavailableOption =
    extra.on_unavailable === 'continue_to_rules' ? 'continue_to_rules' : 'hold';
  const shadowMode = Boolean(extra.shadow_mode);
  const enrichmentMappings = extra.enrichment_mappings ?? [];
  const matchIdentifiers = extra.match_identifiers ?? [];
  const ssnIdentifier = matchIdentifiers.find(
    identifier => identifier.kind === 'ssn',
  );
  const confirmsWithSsn = Boolean(ssnIdentifier);
  const ssnSource = ssnIdentifier?.source ?? 'ssn';
  const [lastSsnSource, setLastSsnSource] = useState(ssnSource);
  const selectedSsnOption =
    dataKeyOptions.find(option => option.key === ssnSource) ?? null;
  const allowedEnrichmentSources =
    stage.additional_info.allowed_enrichment_sources ?? [];
  const alwaysCopiedDataKeys =
    stage.additional_info.always_copied_data_keys ?? [];
  const enrichmentLimitReached =
    enrichmentMappings.length >= MAX_ENRICHMENT_MAPPINGS;

  const targetCounts = enrichmentMappings.reduce<Record<string, number>>(
    (counts, mapping) => {
      if (!mapping.target) return counts;

      return {
        ...counts,
        [mapping.target]: (counts[mapping.target] ?? 0) + 1,
      };
    },
    {},
  );

  const enrichmentTargetError = (mapping: EnrichmentMapping) => {
    if (!mapping.target.trim()) return messages.enrichmentTargetRequired;
    if (mapping.target === 'hris_id') {
      return messages.enrichmentTargetHrisIdReserved;
    }
    if (mapping.target.startsWith('rehire_check_')) {
      return messages.enrichmentTargetPrefixReserved;
    }
    if (targetCounts[mapping.target] > 1) {
      return messages.enrichmentTargetDuplicate;
    }
    return undefined;
  };

  const hasInvalidEnrichmentMapping = enrichmentMappings.some(mapping =>
    Boolean(enrichmentTargetError(mapping)),
  );

  const hasZeroConnectors = !connectorsLoading && connectors.length === 0;
  const isAutoSelected = connectors.length === 1;

  // AUT-537 visual QA fix: the auto-selected connector must NOT be written into `stage.extra`
  // on mount (that would make a freshly loaded, untouched stage show as dirty with zero user
  // interaction - unlike eligibility_field, this can't be seeded via StageContextProvider's
  // load-time normalization because it depends on the async connectors list, which isn't
  // available there). Instead it's computed here purely for display, and only ever written
  // into `stage.extra` as a backfill inside updateExtra, i.e. at the moment of a genuine edit.
  const persistedConnectorUuid = extra.connector_uuid ?? '';
  // A connector deleted or replaced in the Connector Marketplace leaves its uuid saved here. The
  // lookup never falls back from a saved uuid, so a stale one fails every applicant with
  // `no_connector`. An empty list is a fetch failure or the zero-connector state, never proof.
  const isStaleConnector =
    !connectorsLoading &&
    connectors.length > 0 &&
    Boolean(persistedConnectorUuid) &&
    !connectors.some(connector => connector.uuid === persistedConnectorUuid);
  const effectiveConnectorUuid = isStaleConnector
    ? isAutoSelected
      ? connectors[0].uuid
      : ''
    : persistedConnectorUuid ||
      (isAutoSelected ? (connectors[0]?.uuid ?? '') : '');
  const selectedConnector =
    connectors.find(connector => connector.uuid === effectiveConnectorUuid) ??
    null;
  const {
    metadata,
    loading: metadataLoading,
    refetch: refetchMetadata,
  } = useRehireMetadata(effectiveConnectorUuid, eligibilityField);
  const previousConnectorUuid = useRef(effectiveConnectorUuid);
  const connectorChanged =
    previousConnectorUuid.current !== effectiveConnectorUuid;
  previousConnectorUuid.current = effectiveConnectorUuid;
  const autoSelectedEligibilityField =
    metadataLoading || connectorChanged || metadata.fields.length !== 1
      ? ''
      : metadata.fields[0].key;
  const effectiveEligibilityField =
    eligibilityField || autoSelectedEligibilityField;
  const fieldOptions = metadata.fields.some(
    field => field.key === eligibilityField,
  )
    ? metadata.fields
    : eligibilityField
      ? [{ key: eligibilityField, remoteKey: null }, ...metadata.fields]
      : metadata.fields;
  const selectedEligibilityField =
    fieldOptions.find(field => field.key === effectiveEligibilityField) ?? null;
  const hasNoEligibilityFields =
    !metadataLoading &&
    Boolean(effectiveConnectorUuid) &&
    !metadata.syncState.error &&
    metadata.fields.length === 0 &&
    !eligibilityField;
  const legacyEligibilityField = Boolean(
    eligibilityField &&
    !metadata.fields.some(field => field.key === eligibilityField),
  );
  const observedValueCounts = new Map(
    metadata.observedValues.map(({ value, count }) => [value, count]),
  );
  const valuesStillSyncing =
    !metadataLoading &&
    !metadata.syncState.error &&
    Boolean(effectiveConnectorUuid) &&
    (!metadata.syncState.initialSyncCompleted || metadata.sampleSize === 0);
  const valuesAreEmpty =
    !metadataLoading &&
    metadata.syncState.initialSyncCompleted &&
    metadata.sampleSize > 0 &&
    metadata.observedValues.length === 0;

  useEffect(() => {
    if (ssnIdentifier?.source) {
      setLastSsnSource(ssnIdentifier.source);
    }
  }, [ssnIdentifier?.source]);

  const updateExtra = useCallback(
    (
      updates: Partial<RehireCheckStageExtra>,
      persistAutoSelectedField = true,
    ) => {
      setStage(prevStage =>
        produce(prevStage, draftStage => {
          const currentExtra = draftStage.extra as RehireCheckStageExtra;
          Object.assign(currentExtra, updates);

          // Backfill the auto-selected connector the moment ANY real edit happens (even to an
          // unrelated field), so saving after e.g. toggling shadow mode still persists the
          // auto-selected connector rather than leaving it unset.
          if (!currentExtra.connector_uuid && connectors.length === 1) {
            currentExtra.connector_uuid = connectors[0].uuid;
          }
          if (
            persistAutoSelectedField &&
            !currentExtra.eligibility_field &&
            autoSelectedEligibilityField
          ) {
            currentExtra.eligibility_field = autoSelectedEligibilityField;
          }
        }),
      );
    },
    [setStage, connectors, autoSelectedEligibilityField],
  );

  // Real save-blocking enforcement (frontend-only, per the ticket's descope): a zero-connector
  // stage cannot be saved. Disables the shared Save button via StageContext.canSave and is
  // also enforced defensively inside StageContextProvider#onSaveChanges itself.
  //
  // `stage` is intentionally included below even though the effect body never reads it.
  // StageContextProvider#setStageAndTransformationStates optimistically resets canSave to
  // `true` on every stage-load event (initial mount, switching to a different stage, Discard,
  // and post-save reinit) - see AUT-537. If this effect only re-ran when `hasZeroConnectors`'s
  // *value* changed, the veto would never get re-asserted after a reset that happens to leave
  // that value unchanged: switching between two zero-connector Rehire Check stages, or
  // discarding on the same zero-connector stage, both reset canSave to true without
  // `hasZeroConnectors` ever flipping. Every one of those reset events, however, produces a
  // brand-new `stage` object reference (setStageAndTransformationStates always shallow-copies),
  // even when `external_id` is unchanged (Discard), so keying off `stage` guarantees this
  // effect re-fires and re-asserts the veto immediately after every reset.
  useEffect(() => {
    void stage;
    setCanSave(
      !hasZeroConnectors && !hasInvalidEnrichmentMapping && !isStaleConnector,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasInvalidEnrichmentMapping,
    hasZeroConnectors,
    isStaleConnector,
    setCanSave,
    stage,
  ]);

  // Unlike the AUT-537 display-only auto-select, a stale saved connector is written on load: the
  // saved stage is broken, so it should read as unsaved, and with the dropdown locked there is no
  // other edit that could replace it. The connector list arrives async, so this cannot run in
  // StageContextProvider's load-time normalization.
  const [staleConnectorReplacedFor, setStaleConnectorReplacedFor] = useState<
    string | null
  >(null);
  useEffect(() => {
    if (!isStaleConnector || !isAutoSelected) return;

    updateExtra({ connector_uuid: connectors[0].uuid }, false);
    setStaleConnectorReplacedFor(stage.external_id);
  }, [
    connectors,
    isAutoSelected,
    isStaleConnector,
    stage.external_id,
    updateExtra,
  ]);
  const showStaleConnectorReplaced =
    isDirty && staleConnectorReplacedFor === stage.external_id;

  const onConnectorChange = (connector: RehireConnector | null) => {
    updateExtra({ connector_uuid: connector?.uuid ?? '' }, false);
  };

  const onEligibleValuesChange = (values: string[]) => {
    updateExtra({ eligible_values: values });
  };

  const onSsnConfirmationChange = (enabled: boolean) => {
    updateExtra({
      ...(!enabled ? { require_ssn: false } : {}),
      match_identifiers: enabled
        ? [
            ...matchIdentifiers.filter(identifier => identifier.kind !== 'ssn'),
            { kind: 'ssn', source: lastSsnSource },
          ]
        : matchIdentifiers.filter(identifier => identifier.kind !== 'ssn'),
    });
  };

  const onSsnSourceChange = (source: { key: string } | null) => {
    const nextSsnSource = source?.key ?? 'ssn';
    setLastSsnSource(nextSsnSource);
    updateExtra({
      match_identifiers: [
        ...matchIdentifiers.filter(identifier => identifier.kind !== 'ssn'),
        { kind: 'ssn', source: nextSsnSource },
      ],
    });
  };

  const addEnrichmentMapping = (source: EnrichmentSource) => {
    if (enrichmentLimitReached) return;

    updateExtra({
      enrichment_mappings: [
        ...enrichmentMappings,
        { source, target: DEFAULT_ENRICHMENT_TARGETS[source] },
      ],
    });
  };

  const updateEnrichmentTarget = (index: number, target: string) => {
    updateExtra({
      enrichment_mappings: enrichmentMappings.map((mapping, mappingIndex) =>
        mappingIndex === index ? { ...mapping, target } : mapping,
      ),
    });
  };

  const removeEnrichmentMapping = (index: number) => {
    updateExtra({
      enrichment_mappings: enrichmentMappings.filter(
        (_mapping, mappingIndex) => mappingIndex !== index,
      ),
    });
  };

  return (
    <StageSettingCard
      title={<FormattedMessage {...messages.settingsCardTitle} />}
      isLoading={false}
      variant="default"
    >
      {hasZeroConnectors && (
        <Alert type="danger" fullWidth>
          <FormattedMessage {...messages.zeroConnectorsNotice} />
        </Alert>
      )}
      {isStaleConnector && !isAutoSelected && (
        <Alert type="danger" fullWidth>
          <FormattedMessage {...messages.staleConnectorNotice} />
        </Alert>
      )}
      {showStaleConnectorReplaced && (
        <Alert type="warning" fullWidth>
          <FormattedMessage
            {...messages.staleConnectorReplacedNotice}
            values={{ connectorName: connectors[0]?.name }}
          />
        </Alert>
      )}
      <Grid container direction="column">
        <Grid item className={styles.fieldGroup}>
          <Typography variant="body2" className={styles.fieldLabel}>
            <FormattedMessage {...messages.connectorLabel} />
          </Typography>
          <Autocomplete
            disabled={isAutoSelected || hasZeroConnectors}
            loading={connectorsLoading}
            options={connectors}
            value={selectedConnector}
            getOptionLabel={option => option.name}
            getOptionSelected={(option, value) => option.uuid === value.uuid}
            onChange={(_event, selected) => onConnectorChange(selected)}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                error={hasZeroConnectors}
                placeholder={intl.formatMessage(messages.connectorPlaceholder)}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': intl.formatMessage(messages.connectorLabel),
                }}
                helperText={
                  isAutoSelected ? (
                    <FormattedMessage
                      {...messages.connectorAutoSelectedHelperText}
                    />
                  ) : undefined
                }
              />
            )}
          />
        </Grid>

        {allowReadSecureData && (
          <Grid item className={styles.fieldGroup}>
            <Typography variant="body2" className={styles.fieldLabel}>
              <FormattedMessage {...messages.matchingLabel} />
            </Typography>
            <Typography variant="body2" className={styles.matchingHelperText}>
              <FormattedMessage {...messages.matchingHelperText} />
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmsWithSsn}
                  color="primary"
                  onChange={event =>
                    onSsnConfirmationChange(event.target.checked)
                  }
                />
              }
              label={
                <Typography variant="body2">
                  <FormattedMessage {...messages.ssnConfirmationLabel} />
                </Typography>
              }
            />
            {confirmsWithSsn && (
              <div className={styles.ssnSource}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(extra.require_ssn)}
                      color="primary"
                      onChange={event =>
                        updateExtra({ require_ssn: event.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <FormattedMessage {...messages.requireSsnLabel} />
                    </Typography>
                  }
                />
                <StyledReactSelect
                  options={dataKeyOptions}
                  value={selectedSsnOption}
                  onChange={onSsnSourceChange}
                  label={intl.formatMessage(messages.ssnSourceLabel)}
                  aria-label={intl.formatMessage(messages.ssnSourceLabel)}
                  getOptionLabel={option =>
                    option.key === 'ssn' && option.predefined
                      ? intl.formatMessage(messages.ssnStandardFieldOption)
                      : option.key
                  }
                  isOptionSelected={option => option.key === ssnSource}
                  isSearchable
                  placeholder=""
                />
                <Typography
                  variant="caption"
                  className={styles.ssnSourceHelperText}
                >
                  <FormattedMessage {...messages.ssnSourceHelperText} />
                </Typography>
              </div>
            )}
          </Grid>
        )}

        <Grid item className={styles.fieldGroup}>
          <Typography variant="body2" className={styles.fieldLabel}>
            <FormattedMessage {...messages.eligibilityFieldLabel} />
          </Typography>
          {metadata.syncState.error ? (
            <Alert type="warning" fullWidth>
              <div className={styles.metadataErrorNotice}>
                <Typography variant="body2">
                  <FormattedMessage
                    {...METADATA_ERROR_MESSAGES[
                      metadata.syncState.error.reason
                    ]}
                  />
                </Typography>
                <Button
                  type="secondary"
                  size="small"
                  onClick={refetchMetadata}
                  isLoading={metadataLoading}
                >
                  <FormattedMessage {...messages.retryMetadataButton} />
                </Button>
              </div>
            </Alert>
          ) : hasNoEligibilityFields ? (
            <Alert type="danger" fullWidth>
              <FormattedMessage {...messages.noEligibilityFieldsNotice} />
            </Alert>
          ) : (
            <Autocomplete
              disabled={
                metadataLoading ||
                (metadata.fields.length === 1 && !legacyEligibilityField)
              }
              loading={metadataLoading}
              options={fieldOptions}
              value={selectedEligibilityField}
              getOptionLabel={option => option.key}
              getOptionSelected={(option, value) => option.key === value.key}
              onChange={(_event, selected) =>
                updateExtra({ eligibility_field: selected?.key ?? '' })
              }
              renderOption={option =>
                option.remoteKey
                  ? `${option.key} → ${option.remoteKey}`
                  : option.key
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  error={legacyEligibilityField}
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': intl.formatMessage(
                      messages.eligibilityFieldLabel,
                    ),
                  }}
                  helperText={
                    legacyEligibilityField ? (
                      <FormattedMessage
                        {...messages.legacyEligibilityFieldHelperText}
                      />
                    ) : metadata.fields.length === 1 ? (
                      <FormattedMessage
                        {...messages.eligibilityFieldAutoSelectedHelperText}
                      />
                    ) : (
                      <FormattedMessage
                        {...messages.eligibilityFieldHelperText}
                      />
                    )
                  }
                />
              )}
            />
          )}
        </Grid>

        {!metadata.syncState.error && (
          <Grid item className={styles.fieldGroup}>
            <Typography variant="body2" className={styles.fieldLabel}>
              <FormattedMessage {...messages.eligibleValuesLabel} />
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={
                valuesStillSyncing || valuesAreEmpty
                  ? ([] as string[])
                  : metadata.observedValues.map(({ value }) => value)
              }
              value={eligibleValues}
              onChange={(_event, selected) =>
                onEligibleValuesChange(selected as string[])
              }
              renderTags={(value: string[], getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option}
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderOption={option =>
                `${option} — ${observedValueCounts.get(option) ?? 0} ${intl.formatMessage(messages.terminatedEmployeesLabel)}`
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder={
                    eligibleValues.length === 0
                      ? intl.formatMessage(messages.eligibleValuesPlaceholder)
                      : undefined
                  }
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': intl.formatMessage(
                      messages.eligibleValuesLabel,
                    ),
                  }}
                  helperText={
                    valuesStillSyncing ? (
                      <FormattedMessage
                        {...messages.valuesStillSyncingHelperText}
                      />
                    ) : valuesAreEmpty ? (
                      <FormattedMessage
                        {...messages.emptyObservedValuesHelperText}
                        values={{ sampleSize: metadata.sampleSize }}
                      />
                    ) : (
                      <FormattedMessage
                        {...messages.eligibleValuesHelperText}
                      />
                    )
                  }
                />
              )}
            />
          </Grid>
        )}

        <Grid item className={styles.fieldGroup}>
          <Typography variant="body2" className={styles.fieldLabel}>
            <FormattedMessage {...messages.onUnavailableLabel} />
          </Typography>
          <RadioGroup
            name="rehire-check-on-unavailable"
            value={onUnavailable}
            className={styles.radioOptionsContainer}
          >
            <Grid item>
              <Radio
                labelComponent={
                  <Typography variant="body2" className={styles.radioLabel}>
                    <FormattedMessage {...messages.onUnavailableHold} />
                  </Typography>
                }
                checked={onUnavailable === 'hold'}
                onChange={() => updateExtra({ on_unavailable: 'hold' })}
              />
            </Grid>
            <Grid item>
              <Radio
                labelComponent={
                  <Typography variant="body2" className={styles.radioLabel}>
                    <FormattedMessage {...messages.onUnavailableContinue} />
                  </Typography>
                }
                checked={onUnavailable === 'continue_to_rules'}
                onChange={() =>
                  updateExtra({ on_unavailable: 'continue_to_rules' })
                }
              />
            </Grid>
          </RadioGroup>
        </Grid>

        <Grid item className={styles.shadowModeContainer}>
          <FormControlLabel
            control={
              <Switch
                checked={shadowMode}
                color="primary"
                onChange={event =>
                  updateExtra({ shadow_mode: event.target.checked })
                }
              />
            }
            label={
              <Typography variant="body2">
                <FormattedMessage {...messages.shadowModeLabel} />
              </Typography>
            }
          />
          <Typography variant="caption" className={styles.shadowModeHelperText}>
            <FormattedMessage {...messages.shadowModeHelperText} />
          </Typography>
        </Grid>

        <Grid item className={styles.subCard}>
          <Typography variant="h6" className={styles.subCardTitle}>
            <FormattedMessage {...messages.enrichmentTitle} />
          </Typography>
          <Alert type="warning" className={styles.enrichmentWarning} fullWidth>
            <FormattedMessage {...messages.enrichmentCopyNotice} />
          </Alert>

          <div className={styles.alwaysCopiedBlock}>
            <Typography variant="body2" className={styles.fieldLabel}>
              <FormattedMessage {...messages.alwaysCopiedTitle} />
            </Typography>
            {alwaysCopiedDataKeys.map(dataKey => (
              <div className={styles.alwaysCopiedItem} key={dataKey}>
                {ALWAYS_COPIED_DESCRIPTIONS[dataKey] && (
                  <Typography variant="body1">
                    <FormattedMessage
                      {...ALWAYS_COPIED_DESCRIPTIONS[dataKey]}
                    />
                  </Typography>
                )}
                <Typography variant="body1" className={styles.alwaysCopiedKey}>
                  {dataKey}
                </Typography>
              </div>
            ))}
          </div>

          <div className={styles.optionalEnrichmentHeader}>
            <Typography variant="body2">
              <FormattedMessage {...messages.enrichmentSuggestionsLabel} />
            </Typography>
            <Typography
              variant="body1"
              className={styles.optionalEnrichmentLimit}
            >
              <FormattedMessage {...messages.enrichmentDescription} />
            </Typography>
          </div>
          <div className={styles.enrichmentSuggestions}>
            {allowedEnrichmentSources.map(source => (
              <Chip
                key={source}
                label={intl.formatMessage(ENRICHMENT_SOURCE_LABELS[source])}
                size="small"
                clickable={!enrichmentLimitReached}
                disabled={enrichmentLimitReached}
                onClick={() => addEnrichmentMapping(source)}
              />
            ))}
          </div>
          {enrichmentLimitReached && (
            <Typography
              variant="caption"
              className={styles.enrichmentLimitNotice}
            >
              <FormattedMessage {...messages.enrichmentLimitReached} />
            </Typography>
          )}

          {enrichmentMappings.map((mapping, index) => {
            const targetError = enrichmentTargetError(mapping);
            const sourceLabel = intl.formatMessage(
              ENRICHMENT_SOURCE_LABELS[mapping.source],
            );
            const sourceOccurrence = enrichmentMappings
              .slice(0, index)
              .filter(({ source }) => source === mapping.source).length;

            return (
              <div
                className={styles.enrichmentRow}
                key={`${mapping.source}-${sourceOccurrence}`}
              >
                <div className={styles.enrichmentSource}>
                  <Typography variant="caption">
                    <FormattedMessage {...messages.enrichmentSourceLabel} />
                  </Typography>
                  <Typography variant="body2">{sourceLabel}</Typography>
                </div>
                <Typography
                  aria-hidden="true"
                  className={styles.enrichmentArrow}
                >
                  →
                </Typography>
                <TextField
                  className={styles.enrichmentTarget}
                  fullWidth
                  id={`enrichment-target-${index}`}
                  variant="outlined"
                  size="small"
                  label={intl.formatMessage(messages.enrichmentTargetLabel)}
                  InputLabelProps={{
                    htmlFor: `enrichment-target-${index}`,
                  }}
                  value={mapping.target}
                  error={Boolean(targetError)}
                  helperText={
                    targetError ? intl.formatMessage(targetError) : undefined
                  }
                  onChange={event =>
                    updateEnrichmentTarget(index, event.target.value)
                  }
                />
                <IconButton
                  className={styles.enrichmentRemove}
                  aria-label={intl.formatMessage(
                    messages.enrichmentRemoveMapping,
                    { field: sourceLabel },
                  )}
                  onClick={() => removeEnrichmentMapping(index)}
                >
                  <Icon icon={TrashOutlineIcon} size="xs" color="inherit" />
                </IconButton>
              </div>
            );
          })}
        </Grid>

        <Grid item>
          <DryRunTester
            allowSsn={Boolean(allowReadSecureData)}
            connectorUuid={effectiveConnectorUuid}
            eligibilityField={effectiveEligibilityField}
            eligibleValues={eligibleValues}
          />
        </Grid>
      </Grid>
    </StageSettingCard>
  );
}
